import type { TEntityId } from "./entity-id";

export enum EReactionType {
  LIKE = "like",
  DISLIKE = "dislike",
}

export enum EMeetingPermissionLevel {
  VIEWER = "viewer",
  OWNER = "owner",
}

// NOTE: Mirrors apps/nsai/server/src/types/meeting.types.ts. Each value
// represents a phase in the recorder/processor workflow lifecycle. *_FAILED
// values are terminal failures within that phase. Keep in sync with:
//  - server enum + Postgres CHECK constraint
//  - i18n keys in i18n/locales/{en,ru,ar}/call-assistant{,-extension}.json
//  - frontend-shared/constants/meetings.ts MEETING_STATUS_BADGE_CONFIG
export enum EMeetingStatus {
  // NOTE: Initial state — meeting row written, workflow dispatched but no
  // phase has started yet.
  QUEUED = "queued",

  // NOTE: Workflow runner has picked up the job; pre-record setup is in
  // flight (Bitwarden / image pull). Sits between QUEUED and
  // JOINING_MEETING so the badge isn't stuck at "Queued" for the ~30–60s
  // setup window. Set by recordmeeting.yml's first status step.
  PREPARING = "preparing",

  // NOTE: Bot container is running and the bot is in the lobby waiting
  // for admission. Set by recordmeeting.yml right before `Run Recording`.
  // Replaces the prior pre-admission `RECORDING` so a BOT_NOT_ADMITTED
  // transition never reads as "Recording → Bot not admitted."
  JOINING_MEETING = "joining_meeting",

  // NOTE: Bot was admitted; ffmpeg is actively capturing screen+audio.
  // Flipped to from JOINING_MEETING via the recorder's status-callback
  // helper at the provider's "join confirmed" hook (hangup button
  // visible / participant-joined event).
  RECORDING = "recording",

  // NOTE: Recording finished; uploading WAV/WebM to S3.
  RECORDING_UPLOADING = "recording_uploading",

  // NOTE: Terminal failure during recorder phase (bot couldn't join,
  // codec/ffmpeg error, S3 upload failed).
  RECORDING_FAILED = "recording_failed",

  // NOTE: Processor downloading + ffmpeg-converting an uploaded source
  // file (direct-upload flow only).
  PROCESSING_MEDIA = "processing_media",

  // NOTE: Audio sent to ASR (Whisper/vLLM). Long-running.
  TRANSCRIBING = "transcribing",

  // NOTE: Terminal failure during transcription.
  TRANSCRIPTION_FAILED = "transcription_failed",

  // NOTE: Terminal — the uploaded source file has no audio track at all,
  // so there is nothing to transcribe. Detected up front by processor.yml
  // (ffprobe) before the file reaches ASR. Distinct from
  // TRANSCRIPTION_FAILED (a real ASR error) and intentionally NOT retryable
  // — re-running the same file changes nothing; the user must upload a file
  // that contains sound. Rendered with a dedicated banner that says so.
  NO_AUDIO_TRACK = "no_audio_track",

  // NOTE: Terminal — the audio was processed fine but held no recognizable
  // speech (silence, a muted mic, or the wrong track picked at conversion),
  // so ASR produced an empty transcript. The transcriber answers HTTP 422 so
  // processor.yml flags it precisely instead of retrying and falling to a
  // bare FAILED. Like NO_AUDIO_TRACK it is a "nothing to transcribe" outcome
  // — not retryable, rendered with a dedicated banner. Distinct from
  // TRANSCRIPTION_FAILED (a real ASR error).
  NO_SPEECH_DETECTED = "no_speech_detected",

  // NOTE: Transcript sent to LLM for summary, action items, notes.
  SUMMARIZING = "summarizing",

  // NOTE: Terminal failure during summarization.
  SUMMARIZATION_FAILED = "summarization_failed",

  // NOTE: Terminal success — fully processed and available to the user.
  PROCESSED = "processed",

  // NOTE: Terminal — workflow was manually cancelled (admin/user killed
  // the GitHub/Gitea run). Distinct from FAILED: cancellation is
  // intentional, not an error.
  CANCELLED = "cancelled",

  // NOTE: Terminal — host refused to admit the bot from the lobby
  // (Google Meet "denied your request", Teams "denied access", Jitsi
  // lobby-kick). Distinct from RECORDING_FAILED because nothing failed
  // — the host made an intentional choice. Rendered as a neutral grey
  // badge and excluded from FAILED_MEETING_STATUSES so the UI doesn't
  // surface error-state affordances (retry button, red banner).
  BOT_NOT_ADMITTED = "bot_not_admitted",

  // NOTE: Terminal — the bot was in the lobby but nobody responded at all
  // (neither admitted nor denied) across all retries. Distinct from
  // BOT_NOT_ADMITTED (explicit denial). Same neutral grey badge treatment.
  BOT_LOBBY_TIMEOUT = "bot_lobby_timeout",

  // NOTE: Generic terminal failure not captured by phase-specific *_FAILED.
  FAILED = "failed",
}

export enum EMeetingFilterStatus {
  ALL = "all",
  QUEUED = "queued",
  PREPARING = "preparing",
  JOINING_MEETING = "joining_meeting",
  RECORDING = "recording",
  RECORDING_FAILED = "recording_failed",
  TRANSCRIBING = "transcribing",
  TRANSCRIPTION_FAILED = "transcription_failed",
  NO_AUDIO_TRACK = "no_audio_track",
  NO_SPEECH_DETECTED = "no_speech_detected",
  SUMMARIZING = "summarizing",
  SUMMARIZATION_FAILED = "summarization_failed",
  PROCESSED = "processed",
  CANCELLED = "cancelled",
  BOT_NOT_ADMITTED = "bot_not_admitted",
  BOT_LOBBY_TIMEOUT = "bot_lobby_timeout",
  FAILED = "failed",
  ARCHIVED = "archived",
}

export interface IMeetingReaction {
  reaction: EReactionType;
}

export interface IMeetingOwner {
  uuid: string;
  email: string | null;
  fullName: string | null;
}

export interface IMeetingShareInfo {
  permissionLevel: EMeetingPermissionLevel;
  isOwner: boolean;
  // NOTE: NSAI-5849 — whether the current user may share this meeting onward.
  // Sharing is no longer owner-only: anyone who can open a meeting (owner or
  // share recipient) can pass it on, and the re-share is always view-only.
  // Server-derived, so clients gate the Share control on this rather than
  // re-deriving the rule from `isOwner`. Owner-only actions (public link +
  // password, administering other people's shares) still key off `isOwner`.
  canShare: boolean;
  sharedBy?: string;
  sharedByEmail?: string | null;
  sharedByFullName?: string | null;
}

// NOTE: NSAI-4890 — everything the share dialog and its header actually read
// off a meeting. Narrower than IMeeting on purpose: the Uploads list carries
// these fields (once `owner` rides along on the response) but never the other
// ~25, so demanding a whole IMeeting there forced a second request per row just
// to open the dialog. IMeeting satisfies this structurally, so the Call
// Assistant and meeting-details call sites keep passing what they always have.
export interface IShareMeetingTarget {
  uuid: string;
  title: string;
  startTime: string;
  status: EMeetingStatus;
  owner: IMeetingOwner;
}

export interface IMeeting {
  uuid: string;
  uid: string | null;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string | null;
  recordingUrl: string | null;
  transcript: string | null;
  createdAt: string;
  updatedAt: string;
  language: string | null;
  overview: string | null;
  notes: unknown;
  sentiments: unknown;
  participants: unknown;
  topics: unknown;
  actionItems: unknown;
  // NOTE: NSAI-3360 — the summarizer's complete v2 document: labelled key points
  // (the overview as a list), per-topic intros, main points with nested details
  // and their own timestamp, task assignees, and the resolved speaker identities.
  // Optional because list endpoints omit it, and `null` for meetings summarised
  // before the column existed — in both cases fall back to `overview` / `notes` /
  // `actionItems`, which are still populated exactly as before. Rendering it is
  // tracked separately; see docs/architecture/meeting-summarizer.md.
  summaryJson?: unknown;
  speakerTalktime: unknown;
  transcriptEntries: unknown;
  transcription: string | null;
  isArchived: boolean;
  isUploaded: boolean;
  // NOTE: NSAI-5186 — true when the bot recording was stopped by the
  // recorder's 3h max-duration cap. The meeting still reaches PROCESSED; the
  // clients render a badge (list) / banner (details) explaining the cut.
  isTimeout: boolean;
  // NOTE: NSAI-4372 — whether the current user has favourited this meeting.
  // Per-user (a shared meeting can be favourited by one recipient only), so it
  // reflects the requesting user, not a global property of the meeting.
  isFavourite: boolean;
  status: EMeetingStatus;
  // NOTE: Number of times the user has clicked Retry on this meeting.
  // Capped server-side at MAX_MEETING_RETRY_COUNT (3). Reset to 0 when
  // the workflow callback flips status to PROCESSED. UI uses this with
  // the status to decide between "show retry button" and "show max-
  // retries-reached badge". See utils/meeting-status.ts canRetryMeeting.
  retryCount: number;
  secondConsumed: number;
  meetingReactions: IMeetingReaction[];
  permission: IMeetingShareInfo;
  owner: IMeetingOwner;
}

export interface IPaginatedMeetingsResponse {
  meetings: IMeeting[];
  totalCount: number;
}

export interface ICanRetryMeetingParams {
  status: EMeetingStatus;
  retryCount: number;
}

export enum EMeetingSortOrder {
  NEWEST_FIRST = "desc",
  OLDEST_FIRST = "asc",
}

export interface IGetMeetingsParams {
  page?: number;
  itemsPerPage?: number;
  sortOrder?: EMeetingSortOrder;
  searchQuery?: string;
  status?: EMeetingFilterStatus;
  // NOTE: NSAI-4372 — set by the "Favourites" tab to return only meetings the
  // current user has favourited. Orthogonal to `status`; omitted (undefined)
  // for the default "All" view.
  isFavourite?: boolean;
}

// NOTE: NSAI-4372 — resulting state returned by POST /meetings/:id/favourite,
// so the client can reconcile its optimistic star toggle without a refetch.
export interface IToggleFavouriteMeetingResponse {
  isFavourite: boolean;
}

export interface ICreateMeetingReactionPayload {
  reaction: EReactionType;
}

export interface ICreateMeetingFeedbackPayload {
  type: string;
  category: string;
  rating: number;
  description?: string | null;
}

export interface IUpdateMeetingPayload {
  title?: string;
  overview?: string | null;
  transcriptEntries?: unknown;
}

export type TMeetingSharePermissionLevel = EMeetingPermissionLevel.VIEWER;

export interface IShareMeetingPayload {
  userEmail: string;
  permissionLevel?: TMeetingSharePermissionLevel;
  expiresAt?: string;
}

export interface IMeetingShare {
  uuid: string;
  meetingUuid: string;
  userId: TEntityId;
  userEmail: string | null;
  userFullName: string | null;
  permissionLevel: EMeetingPermissionLevel;
  sharedBy: string;
  sharedByEmail: string | null;
  sharedByFullName: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface IMeetingSharesListResponse {
  shares: IMeetingShare[];
  totalCount: number;
}

// NOTE: Public, password-protected meeting share (NSAI-3853). Lets someone
// without an NSAI account view a recording via a link + password.
export interface IMeetingPublicShare {
  shareToken: string;
  // NOTE: Absolute viewer URL built server-side from FRONTEND_URL (NSAI-5027).
  // Clients with no browser origin — the mobile app — render this directly; the
  // web client prefers its own location.origin, which stays correct behind a
  // custom domain or an on-prem reverse proxy.
  shareUrl: string;
  expiresAt: string | null;
  createdAt: string;
  // NOTE: Since NSAI-5027 the owner can read the password back for as long as
  // the link is live, so this is no longer create-only. `null` means the link
  // predates that change and only its irreversible hash was kept — the owner has
  // to regenerate to get a shareable password again.
  password: string | null;
}

// NOTE: Create always returns the password it just generated, so this narrows
// the nullable field above to a plain string.
export interface ICreateMeetingPublicShareResponse extends IMeetingPublicShare {
  password: string;
}

// NOTE: One row of the owner's "Shared Meetings" list (NSAI-5027) — a live link
// plus enough meeting identity to render the row and navigate to the meeting.
export interface IMeetingPublicShareListItem extends IMeetingPublicShare {
  meetingUuid: string;
  meetingTitle: string;
  meetingStartTime: string | null;
}

export interface IMeetingPublicSharesListResponse {
  shares: IMeetingPublicShareListItem[];
}

export interface ICreateMeetingPublicSharePayload {
  expiresAt?: string | null;
}

export interface IMeetingPublicShareStatusResponse {
  requiresPassword: true;
}

export interface IVerifyMeetingPublicSharePayload {
  password: string;
}

export interface IMeetingPublicShareVerifyResponse {
  accessToken: string;
  expiresInSeconds: number;
}

// NOTE: Full-page state a public-share viewer renders for a link it cannot show.
// Shared by every client so web and mobile classify the same responses (see
// `getShareErrorVariant` in `parser/meeting-share-error.ts`).
export type TShareErrorVariant =
  | "not_found"
  | "revoked"
  | "expired"
  | "generic";

// NOTE: Which message the password gate shows after a failed verify (NSAI-5029).
// Every one of these used to render as "incorrect password", which was wrong for
// three of the four: a rotated password, a rate-limited attempt and a transient
// failure are not the viewer mistyping.
export type TPasswordGateError =
  | "invalid"
  | "password_rotated"
  | "too_many_attempts"
  | "generic_error";

// NOTE: The link is gone for good — render the full-page error screen.
export interface IVerifyScreenFailure {
  kind: "screen";
  variant: TShareErrorVariant;
}

// NOTE: Recoverable by submitting again — keep the gate and explain why.
export interface IVerifyGateFailure {
  kind: "gate";
  error: TPasswordGateError;
}

export type TVerifyFailure = IVerifyScreenFailure | IVerifyGateFailure;

export interface IGetPublicSharedMeetingParams {
  shareToken: string;
  accessToken: string;
}

export interface IVerifyPublicSharedMeetingParams {
  shareToken: string;
  payload: IVerifyMeetingPublicSharePayload;
}

export interface ICreateMeetingPublicShareParams {
  meetingUuid: string;
  payload: ICreateMeetingPublicSharePayload;
}

export interface IUpdateMeetingParams {
  meetingUuid: string;
  payload: IUpdateMeetingPayload;
}

export interface ICreateOrUpdateMeetingReactionParams {
  meetingUuid: string;
  payload: ICreateMeetingReactionPayload;
}

export interface ICreateMeetingFeedbackParams {
  meetingUuid: string;
  payload: ICreateMeetingFeedbackPayload;
}

export interface IShareMeetingParams {
  meetingUuid: string;
  payload: IShareMeetingPayload;
}

export interface IBulkShareMeetingItem {
  userEmail: string;
  permissionLevel?: TMeetingSharePermissionLevel;
  expiresAt?: string;
}

export interface IBulkShareMeetingPayload {
  shares: IBulkShareMeetingItem[];
}

export interface IBulkShareMeetingResult {
  email: string;
  success: boolean;
  error?: string;
}

export interface IBulkShareMeetingResponse {
  results: IBulkShareMeetingResult[];
}

export interface IBulkShareMeetingParams {
  meetingUuid: string;
  payload: IBulkShareMeetingPayload;
}

export interface IBulkRemoveMeetingSharesPayload {
  shareUuids: string[];
}

export interface IBulkRemoveMeetingSharesResultItem {
  uuid: string;
  isSuccess: boolean;
  reason?: string;
}

export interface IBulkRemoveMeetingSharesResponse {
  results: IBulkRemoveMeetingSharesResultItem[];
}

export interface IBulkRemoveMeetingSharesParams {
  meetingUuid: string;
  payload: IBulkRemoveMeetingSharesPayload;
}

export interface IJoinMeetingInstantPayload {
  meetingUrl: string;
  meetingTitle?: string;
  language?: string;
  userTimezone?: string;
}

export interface IJoinMeetingInstantResponse {
  success: boolean;
  message: string;
  inputs: unknown;
  meeting: IMeeting;
}

// NOTE: Per-deployment enablement of the flag-gated meeting providers, read
// from /app-config. Mirrors the flag set in
// apps/nsai/server/src/meetings/meetings.constants.ts GATED_MEETING_PROVIDERS.
export interface IMeetingProviderFlags {
  isYandexTelemostEnabled: boolean;
  isKonturTalkEnabled: boolean;
  isSaluteJazzEnabled: boolean;
}

// NOTE: Data-driven description of a flag-gated provider — its display name,
// the URL pattern that identifies its links, and the flag that enables it.
// Adding a provider is a single entry here (plus its regex and a field on
// IMeetingProviderFlags). Mirrors GATED_MEETING_PROVIDERS in
// apps/nsai/server/src/meetings/meetings.constants.ts — keep both in sync.
export interface IGatedMeetingProvider {
  displayName: string;
  urlRegex: RegExp;
  flagKey: keyof IMeetingProviderFlags;
}

export interface IIsValidMeetingUrlProps {
  value: string;
  isYandexTelemostEnabled: boolean;
  isKonturTalkEnabled: boolean;
  isSaluteJazzEnabled: boolean;
  jitsiCustomDomains: readonly string[];
}

export interface IGetSupportedMeetingProvidersLabelProps {
  isYandexTelemostEnabled: boolean;
  isKonturTalkEnabled: boolean;
  isSaluteJazzEnabled: boolean;
}

// NOTE: Everything a live-meeting form needs to validate links and label the
// supported providers, derived once from /app-config by
// getLiveMeetingValidationConfig so web and mobile share the wiring.
export interface ILiveMeetingValidationConfig {
  isYandexTelemostEnabled: boolean;
  isKonturTalkEnabled: boolean;
  isSaluteJazzEnabled: boolean;
  jitsiCustomDomains: readonly string[];
  providersLabel: string;
}

// NOTE: Handshake payload for the public-share WS namespace (NSAI-5028).
// `token` is the short-lived share-access token the viewer received after
// entering the correct password; `shareToken` is the link it was minted for.
// Keep in sync with IMeetingPublicShareWsAuthPayload on the server.
export interface IMeetingPublicShareWsAuthPayload {
  token: string;
  shareToken: string;
}

// NOTE: Payload for EMeetingPublicShareWsEvent.SHARE_REVOKED — the link the
// viewer is watching through is no longer usable. Carries the share token so a
// client holding more than one socket can tell them apart.
export interface IMeetingShareRevokedEventPayload {
  shareToken: string;
}
